import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Team } from "@common/features/Team";
import { api } from "@common/helpers/api";
import { convertKeyValueToArray } from "@common/helpers/functions";
import { SuccessResponse } from "@common/interfaces/Api";
import {
  DataResponse,
  Language,
  Permissions,
  User,
  Webservice,
} from "../interfaces";

export const meKeys = createQueryKeys("me", {
  data: null,
  activities: null,
  languages: null,
  timezones: null,
  user: null,
  webservice: null,
});

export const useFetchMeData = <TData = DataResponse>(
  select?: (data: DataResponse) => TData,
  enabled?: boolean,
  throwOnError = true,
) =>
  useQuery({
    ...meKeys.data,
    queryFn: async () => {
      const response = await api.get<DataResponse>("me/data", {
        timeout: 30000,
      });
      return response.data;
    },
    select,
    staleTime: 5 * 60000,
    enabled,
    refetchOnWindowFocus: "always",
    throwOnError,
  });

export const useFetchLanguages = () =>
  useQuery({
    ...meKeys.languages,
    queryFn: async () => {
      const response = await api.get<Language[]>("me/language");
      return response.data;
    },
  });

export const useFetchTimezones = () =>
  useQuery({
    ...meKeys.timezones,
    queryFn: async () => {
      const response = await api.get<{ [key: string]: string }>("me/timezone");
      return response.data;
    },
    select: (data) => convertKeyValueToArray(data),
  });

export const useFetchUser = () =>
  useQuery({
    ...meKeys.user,
    queryFn: async () => {
      const response = await api.get<User>("me/user");
      return response.data;
    },
  });

export const useFetchWebserivce = () =>
  useQuery({
    ...meKeys.webservice,
    queryFn: async () => {
      const response = await api.get<Webservice>("me/webservice");
      return response.data;
    },
  });

export const useMasterUser = () =>
  useFetchMeData((data) => data.masterUser).data!;

export function updateMasterUnreadCount(
  queryClient: QueryClient,
  key: "comment_unread" | "inbox_unread",
  delta: number,
) {
  queryClient.setQueryData<DataResponse>(meKeys.data.queryKey, (previous) => {
    if (!previous?.masterUser) {
      return previous;
    }

    const currentValue = Number(previous.masterUser[key] || 0);
    const nextValue = Math.max(currentValue + delta, 0);

    if (currentValue === nextValue) {
      return previous;
    }

    return {
      ...previous,
      masterUser: {
        ...previous.masterUser,
        [key]: nextValue,
      },
    } as DataResponse;
  });
}

export const useSiteNotifications = () =>
  useFetchMeData((data) => data.notifications).data!;

export const useUser = () => useFetchMeData((data) => data.user).data!;

export const useHasPermissions = (permission: keyof Permissions) => {
  const { data } = useFetchMeData((data) => data.permissions[permission]);
  return data!;
};

export const useMasterPermissions = () =>
  useFetchMeData((data) => data.masterPermissions).data!;

export const useHasMasterPermissions = (permission: keyof Permissions) => {
  const { data } = useFetchMeData((data) => data.masterPermissions[permission]);
  return data!;
};

export const useIsTeammate = () => {
  const { data } = useFetchMeData((data) => data.is_teammate);
  return data!;
};

export const useHasTeammatePermissions = (
  permission: keyof Team["permissions"],
) => {
  const { data } = useFetchMeData(
    (data) => !data.is_teammate || data.masterUser!.permissions[permission],
  );
  return data!;
};

/** True for a teammate who only sees the conversations assigned to them. */
export const useIsPartialInbox = () => {
  const { data } = useFetchMeData(
    (data) => data.masterUser?.inbox_scope === "partial",
  );
  return Boolean(data);
};

export const useFeatureSupports = () =>
  useFetchMeData((data) => data.featureSupports).data!;

export const useSocialTypes = () =>
  useFetchMeData((data) => data.socialTypes).data!;

export const useFilterMetricPermissions = (metric: string[]): string[] => {
  const mapMetrics: { [key: string]: string } = {
    online_follower_hour: "online_followers",
    mention: "engagement",
  };

  const { data } = useFetchMeData(() =>
    metric.map((item) => (item in mapMetrics ? mapMetrics[item] : item)),
  );
  return data!;
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      type: string;

      name?: string;
      national_code?: string;

      company?: string;
      national_id?: number;
      tax_number?: number;
      registration_number?: number;

      timezones: string;
      language?: string;

      address?: string;
      postal_code?: number;
    }) => api.post<SuccessResponse>("me/profile", data),
    onSuccess: () => {
      queryClient.invalidateQueries(meKeys.user);
    },
  });
};

export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: (data: { password: string; new_password: string }) =>
      api.post<SuccessResponse>("me/password", data),
  });
};

export const useCreateWebservice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<Webservice>("me/webservice").then((response) => response.data),
    onSuccess: (webservice) => {
      queryClient.setQueryData<Webservice>(
        meKeys.webservice.queryKey,
        webservice,
      );

      queryClient.invalidateQueries({
        queryKey: meKeys.webservice.queryKey,
        refetchType: "none",
      });
    },
  });
};

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notifications: User["notifications"]) =>
      api.post<SuccessResponse>("me/notification", { notifications }),
    onSuccess: () => {
      queryClient.invalidateQueries(meKeys.user);
    },
  });
};

export const useUpdateContact = () =>
  useMutation({
    mutationFn: (data: { type: string; value: string; password: string }) =>
      api.put<SuccessResponse>("me/contact", data),
  });

export const useVerifyContact = () =>
  useMutation({
    mutationFn: (data: { type: string; value: string; code: number }) =>
      api.post<SuccessResponse>("me/verify-new-contact", data),
  });

export const useUpdateOauthOnboard = () =>
  useMutation({
    mutationFn: () => api.put<SuccessResponse>("me/oauth-onboard"),
  });

export const useUpdate2FA = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { status: boolean }) => {
      return api.put<SuccessResponse>("me/2fa", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(meKeys.user);
    },
  });
};
